import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField } from "../v3";
import { leadStatusValidationRules } from "../../pages/crm-pro/validation";
import { CRMService } from "../../services";
import { formatPayload } from "../../utils";

export const AddEditLeadStatus = ({ open, onClose, statusId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusDetails, setStatusDetails] = useState({
    name: undefined,
    description: undefined,
  });

  useEffect(() => {
    if (open && statusId) fetchData(statusId);
    else if (open && !statusId) setStatusDetails({});
  }, [open, statusId]);

  const fetchData = async (id) => {
    try {
      const { data: status } = await CRMService.getLeadStatusById(id);
      setStatusDetails({
        name: status.name,
        description: status.description || undefined,
      });
    } catch (error) {
      toast.error("Failed to fetch lead status");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setStatusDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setStatusDetails({});
    setErrors({});
    onClose();
  };

  const handleSaveLeadStatus = async () => {
    setErrors({});
    const payload = formatPayload(statusDetails, !!statusId);
    const validationErrors = leadStatusValidationRules(payload);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      if (statusId) await CRMService.updateLeadStatus(statusId, payload);
      else await CRMService.createLeadStatus(payload);

      toast.success(`Lead status ${statusId ? "updated" : "added"} successfully`);
      if (onSuccess) onSuccess();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || `Error ${statusId ? "updating" : "adding"} lead status`;
      toast.error(errorMessage, {
        type: "error",
      });
      console.error(`Error ${statusId ? "updating" : "adding"} lead status:`, error);
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  return (
    <FormDialog
      open={open}
      title={statusId ? "Edit Lead Status" : "Add Lead Status"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveLeadStatus} disabled={loading} />
        </>
      }
    >
      <Box sx={{ mt: 1, width: 500 }}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Status Name"
            name="name"
            value={statusDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            label="Description"
            name="description"
            value={statusDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={4}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
